/**
 * Hook for tracking recently visited companies via localStorage.
 * Stores the last 5 company IDs, most-recent-first.
 *
 * Covers: AC-020 (recent companies in command palette)
 */

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'dossier:recent-companies';
const MAX_RECENT = 5;

function readFromStorage(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}

function writeToStorage(ids: string[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export interface UseRecentCompaniesReturn {
  recentCompanyIds: string[];
  addRecentCompany: (companyId: string) => void;
  clearRecent: () => void;
}

export function useRecentCompanies(): UseRecentCompaniesReturn {
  const [recentCompanyIds, setRecentCompanyIds] = useState<string[]>(readFromStorage);

  const addRecentCompany = useCallback((companyId: string) => {
    setRecentCompanyIds((prev) => {
      // Remove if already present, then prepend
      const filtered = prev.filter((id) => id !== companyId);
      const updated = [companyId, ...filtered].slice(0, MAX_RECENT);
      writeToStorage(updated);
      return updated;
    });
  }, []);

  const clearRecent = useCallback(() => {
    setRecentCompanyIds([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    recentCompanyIds,
    addRecentCompany,
    clearRecent,
  };
}
